import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { useTranslation } from 'react-i18next'
import etusivu from '../img/datahub/valtuutus-1-etusivu.png'
import tyyppi from '../img/datahub/valtuutus-2-tyyppi.png'
import kayttopaikka from '../img/datahub/valtuutus-3-kayttopaikka.png'
import luo from '../img/datahub/valtuutus-4-luo.png'

interface DatahubGuideDialogProps {
  open: boolean
  authUrl: string
  onClose: () => void
}

const shot = {
  width: 1,
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
  display: 'block',
  mt: 1
}

/**
 * Step-by-step instructions for creating the AP06 authorization in Fingrid's
 * Datahub authorization service, with screenshots of the actual flow.
 */
export function DatahubGuideDialog(props: DatahubGuideDialogProps): JSX.Element {
  const { t } = useTranslation()

  const steps: { text: string, image?: string, alt: string }[] = [
    {
      text: t('Log in to Fingrid Datahub’s authorization service with your bank credentials or mobile certificate (Suomi.fi identification).'),
      alt: ''
    },
    {
      text: t('Select “Voimassa olevat valtuutukset” on the front page, then “Uusi valtuutus +”.'),
      image: etusivu,
      alt: 'Uusi valtuutus +'
    },
    {
      text: t('Choose “Käyttöpaikan tasevastuutiedot” as the authorization type and press “Seuraava”. This authorization shares only balance responsibility information — it gives no access to your consumption data.'),
      image: tyyppi,
      alt: 'Käyttöpaikan tasevastuutiedot'
    },
    {
      text: t('Select your metering point (or all of them) and press “Seuraava”.'),
      image: kayttopaikka,
      alt: 'Valitse käyttöpaikat'
    },
    {
      text: t('Choose “Robustco Oy” as the authorized party, select “Toistaiseksi voimassa oleva” and press “Luo valtuutus”.'),
      image: luo,
      alt: 'Luo valtuutus'
    },
    {
      text: t('Done! The information arrives to us automatically — the status on the settings page updates within about half an hour.'),
      alt: ''
    }
  ]

  return (
    <Dialog open={props.open} onClose={props.onClose} fullWidth maxWidth="sm" data-cy='dialog-Datahub guide'>
      <DialogTitle>{t('Datahub authorization, step by step')}</DialogTitle>
      <DialogContent dividers>
        {steps.map((step, i) => (
          <Box key={i} sx={{ mb: 2.5 }}>
            <Typography variant="body2">
              <b>{i + 1}.</b> {step.text}
            </Typography>
            {i === 0 &&
              <Button
                data-cy='button-Open Datahub'
                variant="contained"
                size="small"
                sx={{ mt: 1 }}
                endIcon={<OpenInNewIcon/>}
                href={props.authUrl}
                target="_blank"
                rel="noopener"
              >
                {t('Open the Datahub authorization service')}
              </Button>}
            {step.image && <Box component="img" src={step.image} alt={step.alt} sx={shot}/>}
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose} data-cy='button-Close Datahub guide'>{t('Close')}</Button>
      </DialogActions>
    </Dialog>
  )
}
